import { useState } from "react";
import { Input } from "@/components/ui/input";
import type { BrokerSymbol } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * M17 — symbol autocomplete over the broker's own instrument list (GET /api/symbols).
 * Hand-rolled on Input + a filtered list, house style — no combobox dependency. Free typing
 * stays legal: the server's resolver remains the authority on what a symbol means, this is
 * assistance toward an exact broker name (an exact pick can never hit the ambiguity 409).
 */
export function SymbolCombobox({
  value,
  symbols,
  onChange,
  onSelect,
  placeholder,
}: {
  value: string;
  /** Empty when the metaapi module is off — the field degrades to a plain input. */
  symbols: BrokerSymbol[];
  onChange: (text: string) => void;
  onSelect: (spec: BrokerSymbol) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  const matches = open ? filterSymbols(symbols, value) : [];

  const pick = (spec: BrokerSymbol) => {
    onSelect(spec);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onFocus={() => setOpen(true)}
        // Delayed so a mousedown on an option lands before the list unmounts.
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (matches.length === 0) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            const spec = matches[highlight];
            if (spec) pick(spec);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        placeholder={placeholder}
        autoCapitalize="characters"
        autoComplete="off"
        role="combobox"
        aria-expanded={matches.length > 0}
      />
      {matches.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover p-1 text-sm shadow-md">
          {matches.map((spec, index) => (
            <li
              key={spec.symbol}
              // mousedown, not click: it fires before the input's blur closes the list.
              onMouseDown={(e) => {
                e.preventDefault();
                pick(spec);
              }}
              onMouseEnter={() => setHighlight(index)}
              className={cn(
                "flex cursor-pointer items-center justify-between rounded px-2 py-1.5",
                index === highlight && "bg-accent",
              )}
            >
              <span className="font-mono">{spec.symbol}</span>
              {spec.profitCurrency && (
                <span className="text-xs text-muted-foreground">{spec.profitCurrency}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Prefix matches first, then substring, capped — the list is assistance, not a browser. */
export function filterSymbols(symbols: BrokerSymbol[], query: string): BrokerSymbol[] {
  const q = query.trim().toUpperCase();
  if (q === "") return [];
  const starts: BrokerSymbol[] = [];
  const contains: BrokerSymbol[] = [];
  for (const spec of symbols) {
    const upper = spec.symbol.toUpperCase();
    if (upper === q) continue; // already typed exactly; a one-item list is noise
    if (upper.startsWith(q)) starts.push(spec);
    else if (upper.includes(q)) contains.push(spec);
  }
  return [...starts, ...contains].slice(0, 8);
}
